import { isJobseekerMember } from '@chat-emp/shared';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import type { FormAnswer, FormNotice, Message, MessageId } from '../fixtures/domain.js';
import { formatClock, formatDaySeparator } from '../util/format.js';
import { isEditable } from '../data/edit-rules.js';
import type { Bounce } from '../data/moderation.js';
import { BouncedNotice, type BouncedActions } from './BouncedNotice.js';
import { MessageBubble } from './MessageBubble.js';
import {
  authorNameFor,
  ourSideAuthor,
  toQuotedPreview,
  type QuotedPreview,
} from './MessageQuote.js';
import styles from './MessageThread.module.css';

interface MessageThreadProps {
  messages: Message[];
  loading: boolean;
  isOwnMessage: (message: Message) => boolean;
  /** The scrolling box itself, owned by the caller: the page and the widget size
   *  and pad their threads differently, but group messages identically. */
  className: string;
  /** Trailing content pinned below the last message, inside the scroll box —
   *  the quick-reply bubble. Flows with the thread rather than floating over it. */
  footer?: ReactNode;
  /** The session user, for the CTA clickability rule. */
  currentUserId: string;
  /** Sends a reply-mode CTA's text. Handed to every bubble, since any of them
   *  may carry one. */
  sendReply: (text: string, quotedMessageId: MessageId, plain?: boolean) => Promise<void>;
  /** Names the other side of the conversation, for quote headers. */
  counterpartName: string;
  /** Absent on an ended chat, which is read-only. */
  onReplyTo?: (message: Message) => void;
  onReport?: (message: Message) => void;
  /** Absent on an ended chat; offered only on bubbles `isEditable` allows. */
  onEdit?: (message: Message) => void;
  /** An unacknowledged bounce, drawn where it happened: after the last message from before it. */
  bounce?: (Bounce & BouncedActions) | null;
  /**
   * The message to open on, when the employer arrived by pressing a search
   * result rather than by picking a conversation. The row showed one line and
   * promised to take them to it; landing at the bottom leaves them to find it
   * again by scrolling, which is the work the search was meant to do. Absent
   * means the ordinary opening, the bottom.
   */
  focusMessageId?: MessageId | null;
}

/** How long a jumped-to message stays lit. */
const FLASH_MS = 1400;

/**
 * The scrolling run of messages, shared by the /chat conversation and the
 * widget panel. Extracted so the grouping rules below have one home — they
 * mirror stream-chat closely enough that a second copy would drift from it
 * quietly, and a widget that grouped messages differently from the page behind
 * it would read as a bug in both.
 *
 * It also owns jumping to a quoted message, because it owns the scroll box and
 * the only complete list of what is currently in it.
 */
export function MessageThread({
  messages,
  loading,
  isOwnMessage,
  className,
  footer,
  currentUserId,
  sendReply,
  counterpartName,
  onReplyTo,
  onReport,
  onEdit,
  bounce = null,
  focusMessageId = null,
}: MessageThreadProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [flashId, setFlashId] = useState<MessageId | null>(null);
  // Scroll to the bottom on a new message and when the footer appears or leaves —
  // not on every footer *content* change (its quick replies re-render as the AI
  // suggestions load), which would yank a reader who has scrolled up.
  const hasFooter = footer != null;

  /**
   * Quotes are resolved against the loaded thread rather than carried inside the
   * reply, so an edited message updates everywhere it was quoted. Memoised on
   * `messages` alone: this component re-renders on every keystroke in the
   * composer above it and on every frame of a jump highlight, none of which
   * change who quoted whom.
   */
  const byId = useMemo(() => new Map(messages.map((message) => [message.id, message])), [messages]);

  /**
   * The search landing already made, if any. A ref, not state: nothing renders
   * differently for it — it exists so each target is jumped to once, and so
   * the scroll-to-bottom below can tell "opens at the bottom" from "opens
   * somewhere the employer asked for". Read at the moment it is asked rather
   * than passed as a dependency, which would re-run the bottom scroll the
   * instant the jump landed and undo it.
   */
  const landedOn = useRef<MessageId | null>(null);
  const landingPending = () => focusMessageId !== null && landedOn.current !== focusMessageId;

  useEffect(() => {
    // Not while a landing is outstanding: the messages arriving is the same
    // commit that would scroll to the bottom, and the jump would have to undo
    // it. A condition rather than effect ordering, which nothing enforces.
    if (landingPending()) return;
    const element = scrollRef.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, [messages, hasFooter]);

  /**
   * Stay pinned when the box itself shrinks — the composer expanding takes a
   * share of the pane (see Composer's `.expanded`), and a window can be dragged
   * shorter. A scroll box that shrinks keeps its `scrollTop`, so the newest
   * message slides down out of sight while nothing about the thread changed.
   *
   * Only re-pins a reader who was already at the bottom: someone who scrolled up
   * to read stays where they are.
   */
  useEffect(() => {
    const element = scrollRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;

    let pinned = true;
    const onScroll = () => {
      pinned = element.scrollHeight - element.scrollTop - element.clientHeight <= 40;
    };
    // A shrink raises the maximum `scrollTop` rather than exceeding it, so the
    // browser fires no scroll event for it and this flag still holds what was
    // true before the resize.
    const observer = new ResizeObserver(() => {
      if (pinned) element.scrollTop = element.scrollHeight;
    });

    element.addEventListener('scroll', onScroll, { passive: true });
    observer.observe(element);
    return () => {
      element.removeEventListener('scroll', onScroll);
      observer.disconnect();
    };
  }, []);

  /**
   * Scroll to a quoted message and light it up.
   *
   * Bails silently when the original is not on screen. A quote can point above
   * the loaded window or at something since deleted, and the honest response to
   * "I cannot take you there" is to do nothing rather than scroll somewhere
   * arbitrary. Clearing `flashId` before setting it restarts the animation when
   * the same quote is tapped twice.
   */
  const jumpToMessage = useCallback((id: MessageId, behavior: ScrollBehavior = 'smooth') => {
    const target = scrollRef.current?.querySelector(`[data-message-id="${CSS.escape(id)}"]`);
    if (!target) return false;
    target.scrollIntoView({ block: 'center', behavior });
    setFlashId(null);
    requestAnimationFrame(() => setFlashId(id));
    return true;
  }, []);

  /**
   * Land on the searched-for message instead of at the bottom. `"auto"` rather
   * than smooth: this is an arrival, not a movement — animating up from a
   * bottom the reader never saw shows them the scroll rather than the message.
   * Once per id; `messages.length` is a dependency because the thread mounts
   * empty and the target does not exist until the window lands.
   */
  useEffect(() => {
    if (!focusMessageId || landedOn.current === focusMessageId) return;
    if (jumpToMessage(focusMessageId, 'auto')) landedOn.current = focusMessageId;
  }, [focusMessageId, messages.length, jumpToMessage]);

  useEffect(() => {
    if (!flashId) return;
    const timer = setTimeout(() => setFlashId(null), FLASH_MS);
    return () => clearTimeout(timer);
  }, [flashId]);

  return (
    /* The whole transcript is masked in a session replay (AI-3016) — it is the
       largest pile of personal data on any screen here, and nothing we want to
       learn from a replay needs the words. At the container, so a new kind of
       message cannot arrive unmasked. */
    <div data-ph-mask className={className} ref={scrollRef}>
      {loading ? (
        <p className={styles.hint}>Loading…</p>
      ) : (
        <>
          {renderThread(messages, {
            byId,
            isOwnMessage,
            currentUserId,
            sendReply,
            counterpartName,
            flashId,
            jumpToMessage,
            onReplyTo,
            onReport,
            onEdit,
            bounce,
          })}
          {footer}
        </>
      )}
    </div>
  );
}

interface ThreadContext {
  byId: Map<MessageId, Message>;
  isOwnMessage: (message: Message) => boolean;
  currentUserId: string;
  sendReply: (text: string, quotedMessageId: MessageId, plain?: boolean) => Promise<void>;
  counterpartName: string;
  flashId: MessageId | null;
  jumpToMessage: (id: MessageId) => void;
  onReplyTo?: (message: Message) => void;
  onReport?: (message: Message) => void;
  onEdit?: (message: Message) => void;
  bounce: (Bounce & BouncedActions) | null;
}

function renderThread(messages: Message[], ctx: ThreadContext): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastDay = '';
  const noticeAfter = ctx.bounce ? lastIndexAtOrBefore(messages, ctx.bounce.at) : null;
  if (ctx.bounce && noticeAfter === -1) nodes.push(bounceNotice(ctx.bounce));

  for (const [index, message] of messages.entries()) {
    const day = new Date(message.createdAt).toDateString();
    const opensDay = day !== lastDay;
    if (opensDay) {
      nodes.push(
        <div key={`day-${message.id}`} className={styles.daySeparator}>
          <span>{formatDaySeparator(message.createdAt)}</span>
        </div>,
      );
      lastDay = day;
    }

    // A system note that opens a day is drawn tight under the pill it belongs
    // to. Both halves of the test matter: a note further down the thread has no
    // pill to pair with, and a *message* under a pill wants the ordinary
    // distance. See `.systemUnderPill`.
    const underPill = message.system && opensDay;

    nodes.push(
      <div
        key={message.id}
        data-message-id={message.id}
        className={messageRowClass(underPill, ctx.flashId === message.id)}
      >
        {namesAuthor(message, messages[index - 1], opensDay, ctx) && (
          <span className={styles.ourSideAuthor}>{ourSideAuthor(message)}</span>
        )}
        <MessageBubble
          message={message}
          own={ctx.isOwnMessage(message)}
          endsGroup={endsGroup(message, messages[index + 1])}
          currentUserId={ctx.currentUserId}
          sendReply={ctx.sendReply}
          quoted={resolveQuote(message, ctx)}
          formAnswers={resolveAnswers(message, ctx)}
          formNotice={resolveNotice(message, ctx)}
          onJumpToMessage={ctx.jumpToMessage}
          // A system note is nobody's message, so there is no one to answer.
          onReplyTo={message.system ? undefined : ctx.onReplyTo}
          onReport={isJobseekerMember(message.senderId) ? ctx.onReport : undefined}
          onEdit={isEditable(message, ctx.currentUserId) ? ctx.onEdit : undefined}
        />
      </div>,
    );
    if (ctx.bounce && index === noticeAfter) nodes.push(bounceNotice(ctx.bounce));
  }

  return nodes;
}

function bounceNotice(bounce: Bounce & BouncedActions): ReactNode {
  return (
    <div key={`bounce-${bounce.messageId}`} className={styles.messageRow}>
      <div className={styles.daySeparator}>
        <span>{formatClock(bounce.at)}</span>
      </div>
      <BouncedNotice {...bounce} />
    </div>
  );
}

/** Index of the last message created at or before `at`; -1 when none is. */
function lastIndexAtOrBefore(messages: Message[], at: string): number {
  const cutoff = Date.parse(at);
  for (let i = messages.length - 1; i >= 0; i--) {
    if (Date.parse(messages[i]!.createdAt) <= cutoff) return i;
  }
  return -1;
}

/**
 * Whether a bubble on our side opens a run written by someone other than me
 * (AI-3136): the company before the cutover, or a colleague. Mine stay unlabelled.
 */
function namesAuthor(
  message: Message,
  prev: Message | undefined,
  opensDay: boolean,
  ctx: ThreadContext,
): boolean {
  if (message.system || message.senderId === ctx.currentUserId || !ctx.isOwnMessage(message)) {
    return false;
  }
  return opensDay || !prev || prev.system || prev.senderId !== message.senderId;
}

/** Not `rowClass`: MessageCtaButtons has one of those, for its button rows. */
function messageRowClass(underPill: boolean, flashing: boolean): string {
  return [styles.messageRow, underPill && styles.systemUnderPill, flashing && styles.flash]
    .filter(Boolean)
    .join(' ');
}

/**
 * The quoted message as the bubble needs it, or nothing.
 *
 * Nothing covers both "quotes no one" and "quotes something we do not have",
 * and the second deserves the same silence as the first: a stub reading
 * "message unavailable" is a row of chrome explaining an absence the reader was
 * not aware of.
 */
function resolveQuote(message: Message, ctx: ThreadContext): QuotedPreview | undefined {
  // An answer to a form replies to the invitation whether or not it carries an
  // explicit quote: the response's own pointer is the reply (mock `repliesTo`).
  const repliesTo = message.quotedMessageId ?? message.formResponse?.noticeMessageId;
  if (!repliesTo) return undefined;
  const original = ctx.byId.get(repliesTo);
  if (!original) return undefined;

  return toQuotedPreview(original, authorNameFor(original, ctx));
}

/**
 * The questions an answer message was given to, read off the message that did
 * the asking — like a quote, it is allowed to find nothing (the asking may be
 * above the loaded window), which the card survives.
 */
function resolveNotice(message: Message, ctx: ThreadContext): FormNotice | undefined {
  if (!message.formResponse) return undefined;
  return ctx.byId.get(message.formResponse.noticeMessageId)?.form;
}

/**
 * The other direction: the answers an invitation's seal points at, so the card
 * that asked opens onto what was picked. Same tolerance as `resolveNotice`.
 */
function resolveAnswers(message: Message, ctx: ThreadContext): FormAnswer[] | undefined {
  const responseId = message.form?.responseMessageId;
  if (!responseId) return undefined;
  return ctx.byId.get(responseId)?.formResponse?.answers;
}

/**
 * Whether this message is the last of a run by one sender, and so the one that
 * carries the timestamp and receipt for the run. Mirrors stream-chat, which
 * lets the SDK's `groupStyles` collapse consecutive messages and shows the
 * metadata only on the last — a timestamp per message turns a two-line
 * exchange into six lines of chrome.
 *
 * Runs break on a new sender, a system note, or a new day. Deliberately not on
 * a time gap: stream-chat leaves `maxTimeBetweenGroupedMessages` unset, so two
 * messages hours apart still group if nobody spoke between them.
 */
function endsGroup(message: Message, next: Message | undefined): boolean {
  if (!next || message.system || next.system) return true;
  if (next.senderId !== message.senderId) return true;
  return new Date(next.createdAt).toDateString() !== new Date(message.createdAt).toDateString();
}
